// Dad's life beyond the Royal Navy — Chapter II (Career) and Chapter III (Travels).
//
// Strict rules:
//   - Coordinates are city-centre unless a more specific landmark is named.
//   - Places that already appear in the Navy voyages are NOT duplicated here.
//   - "Country" is the modern country name.
//   - The same place can appear under both Business and Leisure if Dad
//     visited for both reasons — but we'll dedupe on the map.

const IMG_BASE = "/images";


// ---------------------------------------------------------------------------
// CHAPTER II — Career as a Technical Author & Information Manager
// ---------------------------------------------------------------------------
//
// Sourced from Dad's own CV (dated 27 January 2014, "Murrayfield Data
// Services" letterhead), plus retirement context provided by the family.
//
// Each "stage" is a major employer / engagement era. Stages that span many
// distinct client projects (notably the independent IM-contractor era from
// 1997 onwards) carry a `projects` list, so the timeline reads like a
// career narrative rather than a flat list of years.

export const CAREER_TIMELINE = [
  {
    id: "mark-one",
    dates: "1978 – 1984",
    role: "Technical Author",
    employer: "Mark One Publications",
    location: "Weybridge, Surrey",
    summary:
      "Straight out of the Royal Navy and into the written word — producing technical documentation for industry. The same engineering precision that ran a Polaris submarine's manoeuvring room, applied to the page.",
  },
  {
    id: "odl-general-manager",
    dates: "May 1984 – May 1993",
    role: "General Manager",
    employer: "Offshore Design Ltd",
    location: "Aberdeen, Scotland",
    summary:
      "The move to Aberdeen. Overall supervision and control of all Technical Publications — Authorship, Graphics, Illustration, Desktop Publishing, Video Services and Reprographics. Built quality control procedures from the ground up, culminating in BS 5750 accreditation across every department.",
    detail:
      "Also responsible for business development, client contact, sales and contract liaison. Company turnover at this time: £10m.",
    honours: [
      {
        id: "aberdeen-burgess-1991",
        date: "14 January 1991",
        title: "Free Burgess and Guild Member of the Burgh of Aberdeen",
        body:
          "Admitted at Aberdeen in the presence of the Lord Provost and other Members of the City of Aberdeen District Council — a civic honour with roots in medieval Scotland, granted for standing and service to the burgh. Terence was admitted as 'General Manager in Aberdeen', paid Five Pounds Sterling to the Common Good, Twenty Pounds Sterling to the Guild Box, and Five Shillings Scots to the Lord Provost in a White Purse, 'as use is'. He became solemnly bound to discharge every civil duty incumbent by law on a true and faithful Burgess of the said Burgh.",
        motto: "Bon Accord",
        images: [
          {
            src: `${IMG_BASE}/Dad_Aberdeen_Guild_Chain_2019.jpg`,
            caption:
              "Dad wearing the Guild Member's silver chain of office — Aberdeen, 2019.",
            isDad: true,
          },
          {
            src: `${IMG_BASE}/Dad_Aberdeen_Burgess_1991.jpg`,
            caption:
              "The Burgess Ticket — bearing Aberdeen's coat of arms and the burgh's motto Bon Accord, signed by the Chief Executive.",
          },
        ],
      },
    ],
  },
  {
    id: "odl-rd-manager",
    dates: "May 1993 – April 1997",
    role: "Research & Development Manager",
    employer: "Offshore Design Ltd",
    location: "Aberdeen, Scotland",
    summary:
      "After the company's restructure, given corporate responsibility for new methods of information management and presentation — pioneering SGML (ISO 8879), HTML, internet and intranet delivery, STEP (ISO 10303), and PDF authoring with Adobe Acrobat. Computer-Based Training using Authorware Professional and Macromind Director.",
    detail:
      "“My work within R&D was mostly self-supporting and the department was actually operating at a profit!” During this period the work diversified beyond the oil industry into the Ministry of Defence, Transportation, and Pharmaceutical sectors.",
  },
  {
    id: "im-contractor-era",
    dates: "1997 – 2014",
    role: "Information Management Contractor",
    employer: "Murrayfield Data Services",
    location: "Aberdeen — Worldwide",
    summary:
      "Independent practice as a multidiscipline Information Management Contractor — user analysis, specification and implementation of systems for the creation, management, storage and delivery of engineering and operational information. Seventeen years, six major client engagements, four continents.",
    detail:
      "Specialism: migrating Asset Information from multiple project data sources into Operations systems, and the preparation of green-field information for handover into operating environments — documents, data, and 3D PDMS models, alongside integration with Maximo and management-of-change processes.",
    projects: [
      {
        id: "elf-elgin-franklin",
        dates: "1998 – 1999",
        client: "Elf — Elgin Franklin",
        location: "North Sea",
        body:
          "Integration of a Notia (Intergraph proprietary software) engineering data warehouse on the Elgin Franklin project, with Documentum and Maximo. Created a reference data library of equipment classifications and attributes based on the NORSOK standard, 250 virtual datasheets, a complete tag hierarchy and equipment relationship model, and an asset register to migrate project data into the operations warehouse.",
      },
      {
        id: "arco-bp-boqueron",
        dates: "2000 – 2001",
        client: "ARCO / BP — Boqueron",
        location: "Venezuela",
        body:
          "Analysis of user requirements on the Arco (later BP) Boqueron project, including the specification and implementation of Documentum as their EDMS and the migration of project data into the EDMS for the Operations phase. Liaison with Boqueron Operations to facilitate handover of data and documentation.",
      },
      {
        id: "bp-gulf-of-mexico",
        dates: "2001 – 2004",
        client: "BP Gulf of Mexico Deepwater",
        location: "BP Westlake, Houston",
        body:
          "Project Manager / Team Leader for the specification and site implementation of an Engineering Data warehouse for the Mad Dog, Holstein, Thunder Horse and Atlantis deepwater projects. Authored the Engineering Information handover strategy. Integrated Maximo, Documentum and Intergraph Notia into a single information management system across all data and document attributes. Coordinated efforts between BP and Mustang Engineering.",
        aside: "“This often led to losing my will to live.”",
      },
      {
        id: "bp-angola-project",
        dates: "2004 – 2006",
        client: "BP Angola — Greater Plutonio (project phase)",
        location: "Aberdeen / Sunbury (initially part-time)",
        body:
          "Lead Engineer for the pilot, proof-of-concept and site implementation of a VNET Engineering Portal for the Greater Plutonio Project — integrating Documentum, the structured engineering data repository, and the PDMS 3D model into a single source of technical information. The portal exposed plot plans, isometrics, intelligent P&IDs, the structured tag register, line list and cable schedule, with full tag-to-document linking across the FPSO.",
      },
      {
        id: "bp-plutonio-fpso",
        dates: "April 2006 – December 2010",
        client: "BP Greater Plutonio FPSO",
        location: "Luanda, Angola",
        body:
          "Senior Consultant / Project Manager. Asked to join the Operations team to prepare the Angola local facilities and the FPSO to accept handover information. What was scoped as a clean handover proved a much larger task — managing the quality and completeness of the handed-over information, recruiting and training the Angola IM team, and running all IM activities for roughly three years through commissioning, first oil, and into full production.",
      },
      {
        id: "bp-sor",
        dates: "January 2011 – August 2013",
        client: "BP Safety & Operational Risk",
        location: "Sunbury (with 3-monthly visits to Luanda)",
        body:
          "Safety & Operational Risk IM Lead. Transferred from Operations into a newly formed business unit — later promoted into the global Regional Safety & Operational Risk function — to develop regional information-management strategy and procedures. Commuted from Aberdeen to Sunbury throughout, with quarterly trips back to Luanda.",
      },
      {
        id: "luanda-qa",
        dates: "August 2013 – 2014",
        client: "BP Luanda Operations QA / Procurement Debottlenecking",
        location: "Luanda, Angola",
        body:
          "GAP analysis between Maximo / DMS and the Engineering Warehouse, identifying discrepancies in POQA information for procurement activities — Maximo short and long descriptions, model numbers, serial numbers, vendors and manufacturers. Prioritised data collection to improve procurement quality, then collected and loaded the corrected data back into Operations systems.",
      },
    ],
  },
  {
    id: "retirement",
    dates: "2014 onwards",
    role: "Retirement",
    employer: "Earned",
    location: "At home, with family",
    summary:
      "Forty-eight years after stepping ashore from HMS Renown, finally hung up the hard hat for good. Murrayfield Data Services wound down. Time at last for golf in the Algarve and grandchildren.",
  },
];

// ---------------------------------------------------------------------------
// CHAPTER III — Travels (Business + Leisure)
// ---------------------------------------------------------------------------
// Tags:
//   - "business": visited for work as an Information Management Consultant
//   - "leisure": visited on holiday with family

export const TRAVELS = [
  // === BUSINESS (Information Management Consultant) ===
  { name: "St John's", lat: 47.5615, lng: -52.7126, country: "Newfoundland, Canada", tags: ["business"] },
  { name: "Vancouver", lat: 49.2827, lng: -123.1207, country: "Canada", tags: ["business"] },
  { name: "Halifax", lat: 44.6488, lng: -63.5752, country: "Nova Scotia, Canada", tags: ["business"] },
  { name: "Toronto", lat: 43.6532, lng: -79.3832, country: "Canada", tags: ["business"] },
  { name: "Huntsville", lat: 45.3266, lng: -79.2178, country: "Ontario, Canada", tags: ["business"] },
  { name: "Abu Dhabi", lat: 24.4539, lng: 54.3773, country: "UAE", tags: ["business"] },
  { name: "Cape Town", lat: -33.9249, lng: 18.4241, country: "South Africa", tags: ["business"] },
  { name: "Johannesburg", lat: -26.2041, lng: 28.0473, country: "South Africa", tags: ["business"] },
  { name: "Durban", lat: -29.8587, lng: 31.0218, country: "South Africa", tags: ["business"] },
  { name: "Mossel Bay", lat: -34.1831, lng: 22.1461, country: "South Africa", tags: ["business"] },
  { name: "Perth", lat: -31.9523, lng: 115.8613, country: "Australia", tags: ["business"] },
  { name: "Luanda", lat: -8.8390, lng: 13.2894, country: "Angola", tags: ["business"] },
  { name: "Maturín", lat: 9.7457, lng: -63.1834, country: "Venezuela", tags: ["business"] },
  { name: "Caracas", lat: 10.4806, lng: -66.9036, country: "Venezuela", tags: ["business"] },
  { name: "Los Angeles", lat: 34.0522, lng: -118.2437, country: "USA", tags: ["business"] },
  { name: "Seoul", lat: 37.5665, lng: 126.9780, country: "South Korea", tags: ["business"] },
  { name: "Ulsan", lat: 35.5384, lng: 129.3114, country: "South Korea", tags: ["business"] },
  { name: "Oslo", lat: 59.9139, lng: 10.7522, country: "Norway", tags: ["business"] },
  { name: "Stavanger", lat: 58.9700, lng: 5.7331, country: "Norway", tags: ["business"] },
  { name: "Valparaíso", lat: -33.0472, lng: -71.6127, country: "Chile", tags: ["business"], navyOverlap: true },

  // === LEISURE (family holidays) ===
  // Caribbean
  { name: "Jamaica", lat: 18.1096, lng: -77.2975, country: "", tags: ["leisure"] },
  { name: "Antigua", lat: 17.0608, lng: -61.7964, country: "", tags: ["leisure"] },
  { name: "Cozumel", lat: 20.4230, lng: -86.9223, country: "Mexico", tags: ["leisure"] },
  { name: "Cancún", lat: 21.1619, lng: -86.8515, country: "Mexico", tags: ["leisure"] },
  { name: "Port-au-Prince", lat: 18.5944, lng: -72.3074, country: "Haiti", tags: ["leisure"] },
  // USA
  { name: "Miami", lat: 25.7617, lng: -80.1918, country: "USA", tags: ["leisure"], navyOverlap: true },
  { name: "Fort Lauderdale", lat: 26.1224, lng: -80.1373, country: "USA", tags: ["leisure"] },
  { name: "Orlando", lat: 28.5384, lng: -81.3789, country: "USA", tags: ["leisure"] },
  { name: "Kissimmee", lat: 28.2920, lng: -81.4076, country: "USA", tags: ["leisure"] },
  { name: "Cape Canaveral", lat: 28.4053, lng: -80.6051, country: "USA", tags: ["leisure"] },
  { name: "Jacksonville", lat: 30.3322, lng: -81.6557, country: "USA", tags: ["leisure"] },
  { name: "Charleston", lat: 32.7765, lng: -79.9311, country: "USA", tags: ["leisure"] },
  { name: "Washington, D.C.", lat: 38.9072, lng: -77.0369, country: "USA", tags: ["leisure"] },
  { name: "New York", lat: 40.7128, lng: -74.0060, country: "USA", tags: ["leisure"] },
  { name: "San Francisco", lat: 37.7749, lng: -122.4194, country: "USA", tags: ["leisure"] },
  { name: "Las Vegas", lat: 36.1699, lng: -115.1398, country: "USA", tags: ["leisure"] },
  { name: "Houston", lat: 29.7604, lng: -95.3698, country: "USA", tags: ["leisure"] },
  { name: "Galveston", lat: 29.3013, lng: -94.7977, country: "USA", tags: ["leisure"] },
  { name: "San Antonio", lat: 29.4241, lng: -98.4936, country: "USA", tags: ["leisure"] },
  // Europe — Iberia & Mediterranean
  { name: "Lisbon", lat: 38.7223, lng: -9.1393, country: "Portugal", tags: ["leisure"], navyOverlap: true },
  { name: "Faro", lat: 37.0194, lng: -7.9304, country: "Portugal", tags: ["leisure"] },
  { name: "Portimão", lat: 37.1366, lng: -8.5378, country: "Portugal", tags: ["leisure"], note: "For the golf." },
  { name: "Lagos", lat: 37.1028, lng: -8.6741, country: "Portugal", tags: ["leisure"], note: "For the golf." },
  { name: "Málaga", lat: 36.7213, lng: -4.4214, country: "Spain", tags: ["leisure"] },
  { name: "Marbella", lat: 36.5108, lng: -4.8856, country: "Spain", tags: ["leisure"] },
  { name: "Fuengirola", lat: 36.5414, lng: -4.6244, country: "Spain", tags: ["leisure"] },
  { name: "Barcelona", lat: 41.3851, lng: 2.1734, country: "Spain", tags: ["leisure"] },
  { name: "Tenerife", lat: 28.2916, lng: -16.6291, country: "Canary Islands, Spain", tags: ["leisure"] },
  { name: "Gran Canaria", lat: 27.9202, lng: -15.5474, country: "Canary Islands, Spain", tags: ["leisure"] },
  // Europe — Italy & France & Greece
  { name: "Marseille", lat: 43.2965, lng: 5.3698, country: "France", tags: ["leisure"] },
  { name: "Rome", lat: 41.9028, lng: 12.4964, country: "Italy", tags: ["leisure"] },
  { name: "Naples", lat: 40.8518, lng: 14.2681, country: "Italy", tags: ["leisure"], navyOverlap: true },
  { name: "Pisa", lat: 43.7228, lng: 10.4017, country: "Italy", tags: ["leisure"] },
  { name: "Civitavecchia", lat: 42.0936, lng: 11.7956, country: "Italy", tags: ["leisure"] },
  { name: "Athens", lat: 37.9838, lng: 23.7275, country: "Greece", tags: ["leisure"] },
  { name: "Santorini", lat: 36.3932, lng: 25.4615, country: "Greece", tags: ["leisure"] },
  // Africa & Middle East
  { name: "Tangier", lat: 35.7595, lng: -5.8340, country: "Morocco", tags: ["leisure"] },
  { name: "Nairobi", lat: -1.2921, lng: 36.8219, country: "Kenya", tags: ["leisure"] },
  { name: "Dubai", lat: 25.2048, lng: 55.2708, country: "UAE", tags: ["leisure"] },
];

// Counts (de-duplicated by name) for headers
const _uniqueByName = (arr) => Array.from(new Set(arr.map((p) => p.name)));

export const CAREER_COUNT = CAREER_TIMELINE.length;
export const BUSINESS_PLACES = TRAVELS.filter((p) => p.tags.includes("business"));
export const LEISURE_PLACES = TRAVELS.filter((p) => p.tags.includes("leisure"));
export const TRAVELS_COUNT_UNIQUE = _uniqueByName(TRAVELS).length;
export const BUSINESS_COUNT_UNIQUE = _uniqueByName(BUSINESS_PLACES).length;
export const LEISURE_COUNT_UNIQUE = _uniqueByName(LEISURE_PLACES).length;


// ---------------------------------------------------------------------------
// Royal Caribbean cruises — Crown & Anchor Society history (10 sailings).
// ---------------------------------------------------------------------------
// Reconstructed from Dad's Crown & Anchor history (dates + ship + cruise
// name verified from app screenshots). Port lists assembled from Royal
// Caribbean published deployments and itinerary archives for each sailing
// date. Where an exact port-list could not be confirmed, the most likely
// port set for that named itinerary in that season is used.

export const CRUISES = [
  {
    id: "oasis-2010",
    year: 2010,
    dates: "Jul 10 – Jul 17, 2010",
    ship: "Oasis of the Seas",
    title: "7 Night Western Caribbean Cruise",
    points: 2,
    home: "Fort Lauderdale",
    portsUnknown: true,
    ports: [
      { name: "Fort Lauderdale", lat: 26.1224, lng: -80.1373, country: "USA", role: "departed" },
    ],
  },
  {
    id: "allure-2011",
    year: 2011,
    dates: "Jul 10 – Jul 17, 2011",
    ship: "Allure of the Seas",
    title: "7 Night Eastern Caribbean Cruise",
    points: 14,
    home: "Fort Lauderdale",
    portsUnknown: true,
    ports: [
      { name: "Fort Lauderdale", lat: 26.1224, lng: -80.1373, country: "USA", role: "departed" },
    ],
  },
  {
    id: "serenade-2012",
    year: 2012,
    dates: "Jun 22 – Jul 4, 2012",
    ship: "Serenade of the Seas",
    title: "12 Night Mediterranean Greek Isles",
    points: 12,
    home: "Venice",
    portsUnknown: true,
    ports: [
      { name: "Venice", lat: 45.4408, lng: 12.3155, country: "Italy", role: "departed" },
    ],
  },
  {
    id: "independence-2013",
    year: 2013,
    dates: "Jul 13 – Jul 27, 2013",
    ship: "Independence of the Seas",
    title: "14 Night Italian Mediterranean Cruise",
    points: 28,
    home: "Southampton",
    ports: [
      { name: "Southampton", lat: 50.9097, lng: -1.4044, country: "England", role: "departed" },
      { name: "Gibraltar", lat: 36.1408, lng: -5.3536, country: "" },
      { name: "Villefranche (Nice)", lat: 43.7039, lng: 7.3104, country: "France" },
      { name: "La Spezia (Florence/Pisa)", lat: 44.1025, lng: 9.8240, country: "Italy" },
      { name: "Civitavecchia (Rome)", lat: 42.0936, lng: 11.7956, country: "Italy" },
      { name: "Naples (Capri)", lat: 40.8518, lng: 14.2681, country: "Italy" },
      { name: "Cagliari", lat: 39.2238, lng: 9.1217, country: "Sardinia, Italy" },
      { name: "Cádiz (Seville)", lat: 36.5298, lng: -6.2924, country: "Spain" },
    ],
  },
  {
    id: "allure-2014",
    year: 2014,
    dates: "Sep 14 – Sep 21, 2014",
    ship: "Allure of the Seas",
    title: "7 Night Eastern Caribbean Cruise",
    points: 14,
    home: "Fort Lauderdale",
    ports: [
      { name: "Fort Lauderdale", lat: 26.1224, lng: -80.1373, country: "USA", role: "departed" },
      { name: "Nassau", lat: 25.0443, lng: -77.3504, country: "Bahamas" },
      { name: "Charlotte Amalie", lat: 18.3419, lng: -64.9307, country: "St Thomas, USVI" },
      { name: "Philipsburg", lat: 18.0237, lng: -63.0458, country: "St Maarten" },
    ],
  },
  {
    id: "anthem-2015-spring",
    year: 2015,
    dates: "Apr 30 – May 3, 2015",
    ship: "Anthem of the Seas",
    title: "3 Night Royal Experience, Paris & Bruges",
    points: 6,
    home: "Southampton",
    note: "Among the very first sailings of Anthem of the Seas — she was christened in Southampton just ten days earlier.",
    ports: [
      { name: "Southampton", lat: 50.9097, lng: -1.4044, country: "England", role: "departed" },
      { name: "Zeebrugge (Bruges)", lat: 51.3367, lng: 3.2058, country: "Belgium" },
      { name: "Le Havre (Paris)", lat: 49.4944, lng: 0.1079, country: "France" },
    ],
  },
  {
    id: "anthem-2015-autumn",
    year: 2015,
    dates: "Oct 11 – Oct 24, 2015",
    ship: "Anthem of the Seas",
    title: "13 Night Sunshine in the Canaries Cruise",
    points: 13,
    home: "Southampton",
    ports: [
      { name: "Southampton", lat: 50.9097, lng: -1.4044, country: "England", role: "departed" },
      { name: "Vigo", lat: 42.2406, lng: -8.7207, country: "Spain" },
      { name: "Lisbon", lat: 38.7223, lng: -9.1393, country: "Portugal" },
      { name: "Madeira (Funchal)", lat: 32.6669, lng: -16.9241, country: "Portugal" },
      { name: "Tenerife (Santa Cruz)", lat: 28.4636, lng: -16.2518, country: "Canary Islands, Spain" },
      { name: "Gran Canaria (Las Palmas)", lat: 28.1235, lng: -15.4366, country: "Canary Islands, Spain" },
      { name: "Lanzarote (Arrecife)", lat: 28.9630, lng: -13.5477, country: "Canary Islands, Spain" },
    ],
  },
  {
    id: "harmony-2016",
    year: 2016,
    dates: "Jul 17 – Jul 24, 2016",
    ship: "Harmony of the Seas",
    title: "7 Night Western Mediterranean Cruise",
    points: 7,
    home: "Barcelona",
    note: "Inaugural-summer sailing of Harmony of the Seas — she had only been at sea since May.",
    ports: [
      { name: "Barcelona", lat: 41.3851, lng: 2.1734, country: "Spain", role: "departed" },
      { name: "Palma de Mallorca", lat: 39.5696, lng: 2.6502, country: "Spain" },
      { name: "Provence (Marseille)", lat: 43.2965, lng: 5.3698, country: "France" },
      { name: "La Spezia (Florence/Pisa)", lat: 44.1025, lng: 9.8240, country: "Italy" },
      { name: "Civitavecchia (Rome)", lat: 42.0936, lng: 11.7956, country: "Italy" },
      { name: "Naples", lat: 40.8518, lng: 14.2681, country: "Italy" },
    ],
  },
  {
    id: "independence-2019",
    year: 2019,
    dates: "Sep 2 – Sep 14, 2019",
    ship: "Independence of the Seas",
    title: "12 Night Spanish Mediterranean Cruise",
    points: 12,
    home: "Southampton",
    ports: [
      { name: "Southampton", lat: 50.9097, lng: -1.4044, country: "England", role: "departed" },
      { name: "Vigo", lat: 42.2406, lng: -8.7207, country: "Spain" },
      { name: "Lisbon", lat: 38.7223, lng: -9.1393, country: "Portugal" },
      { name: "Ibiza", lat: 38.9067, lng: 1.4206, country: "Spain" },
      { name: "Palma de Mallorca", lat: 39.5696, lng: 2.6502, country: "Spain" },
      { name: "Barcelona", lat: 41.3851, lng: 2.1734, country: "Spain" },
      { name: "Cartagena", lat: 37.6056, lng: -0.9819, country: "Spain" },
      { name: "Cádiz (Seville)", lat: 36.5298, lng: -6.2924, country: "Spain" },
    ],
  },
  {
    id: "odyssey-2025",
    year: 2025,
    dates: "Sep 21 – Sep 28, 2025",
    ship: "Odyssey of the Seas",
    title: "7 Night Greek Isles Cruise",
    points: 7,
    home: "Civitavecchia (Rome)",
    ports: [
      { name: "Civitavecchia (Rome)", lat: 42.0936, lng: 11.7956, country: "Italy", role: "departed" },
      { name: "Santorini", lat: 36.3932, lng: 25.4615, country: "Greece" },
      { name: "Kuşadası (Ephesus)", lat: 37.8579, lng: 27.2610, country: "Turkey" },
      { name: "Mykonos", lat: 37.4467, lng: 25.3289, country: "Greece" },
      { name: "Naples (Capri)", lat: 40.8518, lng: 14.2681, country: "Italy" },
    ],
  },
];

// Flatten every cruise port into a single list (deduped by coordinate),
// for use on the World Atlas as a 4th layer.
export const CRUISE_PORTS = (() => {
  const seen = new Map();
  for (const c of CRUISES) {
    for (const p of c.ports) {
      const key = `${Math.round(p.lat * 100)}|${Math.round(p.lng * 100)}`;
      if (!seen.has(key)) {
        seen.set(key, { ...p, ship: c.ship, cruise: c.title, dates: c.dates });
      }
    }
  }
  return Array.from(seen.values());
})();

export const CRUISES_COUNT = CRUISES.length;
export const CRUISE_PORTS_UNIQUE = CRUISE_PORTS.length;
export const CRUISE_NIGHTS_TOTAL = CRUISES.reduce(
  (sum, c) => sum + parseInt(c.title.match(/(\d+)\s*Night/i)?.[1] || "0", 10),
  0
);
